import { useState, useEffect } from 'react';
import { Sun, Moon, User } from 'lucide-react';
import { getUser, refreshAuth } from '../../lib/auth.js';
import { useTheme } from '../../lib/theme.jsx';
import NotificationCenter from '../common/NotificationCenter.jsx';

function Topbar() {
  const { isDark, toggleTheme } = useTheme();
  const [user, setUser] = useState({
    firstName: '',
    role: '',
    loading: true
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setUser(prev => ({ ...prev, loading: true }));
        
        // Get current user from auth
        const currentUser = getUser();
        
        let userData = currentUser;
        
        if (!currentUser) {
          // Try to refresh auth data
          const refreshResult = await refreshAuth();
          if (refreshResult.success) {
            userData = refreshResult.user;
          }
        }
        
        if (userData) {
          try {
            if (userData.id && !isNaN(userData.id)) {
              const token = localStorage.getItem('srpf_auth_token');
              
              const response = await fetch(`${import.meta.env.VITE_API_URL}/users/${parseInt(userData.id)}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const detailedUser = await response.json();
                userData = { ...userData, ...detailedUser.data };
              }
            }
          } catch (profileError) {
            console.log('Could not fetch detailed profile, using basic data:', profileError);
          }
          
          // Get only first name
          let firstName = '';
          if (userData.first_name) {
            firstName = userData.first_name;
          } else if (userData.full_name) {
            firstName = userData.full_name.split(' ')[0] || '';
          }
          
          setUser({
            firstName,
            role: userData.role_name || userData.role || '',
            loading: false
          });
        } else {
          setUser(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        setUser(prev => ({ ...prev, loading: false }));
      }
    };

    fetchUserData();
  }, []);

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 flex items-center justify-between px-6 flex-shrink-0">
      {/* Left side */}
      <div className="flex items-center space-x-4">
        {/* Logo/Brand */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-teal-700 rounded-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white rounded-sm"></div>
          </div>
          <span className="text-lg font-semibold text-teal-700">SRPF</span>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center space-x-4">
        {/* Notifications */}
        <NotificationCenter />

        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* User menu */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-teal-700 rounded-full flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.loading ? 'Loading...' : (user.firstName || 'User')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {user.loading ? '' : (user.role || 'user')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Topbar;
